import Image from "next/legacy/image";
import { Dispatch, useEffect } from "react";
import { motion } from "framer-motion";
import WIconButton from "../wicon-button/WIconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import clsx from "clsx";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

interface Props {
  title?: string;
  subtitle?: string;
  className?: string;
  setModal: Dispatch<string>;
  onClick?: () => void;
  imageSrc?: string;
  imageAlt?: string;
  unclosable?: boolean;
  children?: React.ReactNode;
  icon?: IconName;
  iconColor?: string;
  bottomModal?: boolean;
  large?: boolean;
  noSpace?: boolean;
  backgroundColor?: string;
}

export const WModal = ({ ...props }: Props) => {
  useEffect(() => {
    // Disable scrolling when modal is open
    document.body.style.overflow = "hidden";
    return () => {
      // Enable scrolling when modal is closed
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <>
      {/* Modal wrapper */}
      <div
        className={`fixed top-0 left-0 z-[999999] flex w-screen h-screen justify-center ${
          props.bottomModal ? "items-end lg:items-center" : "items-center"
        } bg-black/75`}
        onClick={() => {
          if (!props.unclosable) props.setModal("");
        }}
      >
        {/* Modal content wrapper */}
        <div
          className={clsx(
            "relative w-full mx-5",
            props.large ? "lg:w-[1000px]" : "lg:w-[500px]"
          )}
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
        >
          <motion.div
            initial={{ transform: "translateY(10px)", opacity: 0 }}
            animate={{ transform: "translateY(0px)", opacity: 1 }}
            transition={{ duration: 0.3 }}
            className={clsx([
              "relative flex flex-col gap-5 py-7 w-[400px] lg:rounded-lg shadow-lg",
              props.large ? "lg:w-[1000px]" : "lg:w-[500px]",
              props?.noSpace ? "px-0" : "px-5",
              props.className && props.className,
              !props.bottomModal && "rounded-lg",
              "max-h-screen h-auto overflow-y-auto", // Ensure scrollable content within the modal
              "lg:custom-scrollbar", // Optional scrollbar styling
              "pb-10", // Add padding for safe areas (on mobile)
            ])}
            style={{
              backgroundColor:
                props.backgroundColor ||
                "rgb(204 247 255 / var(--tw-bg-opacity))",
            }}
          >
            {/* Optional image */}
            {props.imageSrc && (
              <div className="relative w-full h-12 my-5 lg:w-1/2">
                <Image
                  src={props.imageSrc}
                  alt={props.imageAlt || "image"}
                  layout="fill"
                  objectFit="contain"
                  quality={80}
                />
              </div>
            )}

            {/* Optional icon */}
            {props.icon && (
              <FontAwesomeIcon
                icon={props.icon as IconName}
                className={`text-6xl text-${props.iconColor}`}
              />
            )}

            {/* Title */}
            {props.title && (
              <div className="text-2xl font-bold text-center">
                {props.title}
              </div>
            )}

            {/* Subtitle */}
            {props.subtitle && <p>{props.subtitle}</p>}

            {/* Children content */}
            {props.children}
          </motion.div>

          {/* Close button */}
          {!props.unclosable && (
            <motion.div
              initial={{ transform: "translateY(10px)", opacity: 0 }}
              animate={{ transform: "translateY(0px)", opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="absolute lg:-right-2 lg:-top-2 top-2 right-2 z-[300]"
            >
              <WIconButton
                type="close"
                icon={faXmark}
                onClick={() => props.setModal("")}
              />
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};

export default WModal;
